/**
 * CR229: T5 + T6 - Field Resolution & Data Inspector Support
 * 
 * Provides a flat map of all deal fields with:
 * - Current value
 * - Presence status (present | missing | null)
 * - Source path (where the value came from)
 * 
 * This powers the Data Inspector panel and enables designers to
 * understand exactly what data exists for template binding.
 */

import type { RawDealInput, UIDeal } from "./toUiDeal";
import { isSafeValue, getSafeString } from "./safeValueRendering";

// ================================================================
// FIELD DEBUG TYPES
// ================================================================

export type FieldStatus = "present" | "missing" | "null";

export interface FieldDebug {
  /** Field identifier */
  key: string;
  /** Display label for UI */
  label: string;
  /** Resolved value */
  value: unknown;
  /** Status indicator */
  status: FieldStatus;
  /** Source path where value was found */
  sourcePath: string;
  /** Optional notes */
  notes?: string;
  /** Field category for grouping */
  category: FieldCategory;
}

export type FieldCategory = 
  | "header"
  | "value"
  | "dates"
  | "urls"
  | "fine_print"
  | "brands"
  | "source_meta";

// ================================================================
// FIELD DEFINITIONS
// ================================================================

interface FieldDefinition {
  key: string;
  label: string;
  category: FieldCategory;
  paths: string[]; // Ordered fallback paths to check
  required?: boolean; // If true, missing = warning
}

/**
 * CR282: Field definitions with NESTED-FIRST path resolution
 * 
 * Critical: For value fields (bonus, spend, computed, effective), the
 * canonical schema stores these under `canonical_deal_json.value.*`
 * For dates, they're under `canonical_deal_json.dates.*`
 * 
 * Path order matters - check nested first, then top-level.
 */
const FIELD_DEFINITIONS: FieldDefinition[] = [
  // Header fields
  { key: "merchant", label: "Merchant Name", category: "header", paths: ["promo_merchant_name", "merchant_name", "merchant", "canonical_deal_json.merchant_name"] },
  { key: "headline_mechanic", label: "Headline", category: "header", paths: ["headline_mechanic", "canonical_deal_json.headline_mechanic"] },
  { key: "deal_type", label: "Deal Type", category: "header", paths: ["deal_category", "deal_type", "canonical_deal_json.deal_type"], required: true },
  
  // CR282: Value fields - NESTED FIRST (value.* before top-level)
  { key: "bonus_value_cad", label: "Bonus Value (CAD)", category: "value", paths: [
    "canonical_deal_json.value.bonus_value_cad",  // Nested first (canonical schema)
    "bonus_value_cad",                             // Top-level (API response)
    "bonus_amount",                                // Legacy field
    "canonical_deal_json.bonus_value_cad",        // Top-level in canonical (fallback)
  ] },
  { key: "qualifying_spend_cad", label: "Qualifying Spend (CAD)", category: "value", paths: [
    "canonical_deal_json.value.qualifying_spend_cad",  // Nested first
    "qualifying_spend_cad",
    "spend_amount",
    "canonical_deal_json.qualifying_spend_cad",
  ] },
  { key: "discount_percent", label: "Discount %", category: "value", paths: [
    "canonical_deal_json.value.discount_percent",
    "discount_percent",
    "canonical_deal_json.discount_percent",
  ] },
  // CR303: POINTS fields - check resolved top-level columns FIRST (from view/API)
  // before falling back to nested JSON paths
  { key: "points_amount", label: "Points Amount", category: "value", paths: [
    "points_amount",                               // Top-level (resolved from view)
    "canonical_deal_json.points_amount",           // Top-level in canonical JSON
    "canonical_deal_json.value.points_amount",     // Nested fallback
  ] },
  { key: "points_program_name", label: "Points Program", category: "value", paths: [
    "points_program_name",                         // Top-level (resolved from view)
    "canonical_deal_json.points_program_name",     // Top-level in canonical JSON
    "canonical_deal_json.value.points_program",    // Nested fallback
  ] },
  { key: "points_program_id", label: "Points Program ID", category: "value", paths: [
    "points_program_id",
    "canonical_deal_json.points_program_id",
  ] },
  { key: "computed_value_cad", label: "Computed Value (CAD)", category: "value", paths: [
    "canonical_deal_json.value.computed_value_cad",
    "computed_value_cad",
    "canonical_deal_json.computed_value_cad",
  ] },
  { key: "effective_percent_back", label: "Effective % Back", category: "value", paths: [
    "canonical_deal_json.value.effective_percent_back",
    "effective_percent_back",
    "canonical_deal_json.effective_percent_back",
  ] },
  
  // CR282: Date fields - NESTED FIRST (dates.* before top-level)
  { key: "valid_from", label: "Valid From", category: "dates", paths: [
    "canonical_deal_json.dates.valid_from",  // Nested first
    "valid_from",
    "start_date",
    "canonical_deal_json.valid_from",
  ] },
  { key: "valid_until", label: "Valid Until", category: "dates", paths: [
    "canonical_deal_json.dates.valid_until",  // Nested first
    "valid_until",
    "end_date",
    "canonical_deal_json.valid_until",
  ] },
  // CR292: Date type classification fields
  { key: "dates_type", label: "Date Type", category: "dates", paths: [
    "dates_type",
    "canonical_deal_json.dates.dates_type",
    "canonical_deal_json.dates_type",
  ] },
  { key: "date_phrase", label: "Date Phrase", category: "dates", paths: [
    "date_phrase",
    "canonical_deal_json.dates.date_phrase",
    "canonical_deal_json.date_phrase",
  ] },
  // CR292: Date mode (ONGOING = evergreen, FIXED = has dates)
  { key: "date_mode", label: "Date Mode", category: "dates", paths: [
    "date_mode",
    "canonical_deal_json.dates.date_mode",
    "canonical_deal_json.date_mode",
  ] },
  
  // URL fields
  { key: "claim_url", label: "Claim URL", category: "urls", paths: ["claim_url", "canonical_deal_json.claim_url"] },
  { key: "deal_url", label: "Deal URL", category: "urls", paths: ["deal_url", "deal_link", "canonical_deal_json.deal_link"] },
  { key: "source_url", label: "Source URL", category: "urls", paths: ["source_url", "canonical_deal_json.source_url"] },
  { key: "flyer_deeplink_url", label: "Flyer Deeplink", category: "urls", paths: ["flyer_deeplink_url", "canonical_deal_json.flyer_deeplink_url"] },
  
  // Fine print / exclusions
  { key: "fine_print", label: "Fine Print", category: "fine_print", paths: ["fine_print", "canonical_deal_json.fine_print"] },
  { key: "exclusions", label: "Exclusions", category: "fine_print", paths: ["exclusions", "canonical_deal_json.exclusions"] },
  
  // Brand fields - CR282: Include nested brands structure
  { key: "reward_brand_name", label: "Reward Brand", category: "brands", paths: [
    "reward_brand_name",
    "canonical_deal_json.brands.bonus_brand",
    "purchase_gift_card_brand",
    "gift_card_brand",
    "bonus_merchant",
  ] },
  { key: "purchase_gift_card_brand", label: "Gift Card Brand", category: "brands", paths: [
    "qualifying_product_name",
    "nested_purchase_brand",
    "canonical_deal_json.brands.purchase_brand",
    "canonical_deal_json.qualifying_product_name",
    "purchase_gift_card_brand",
    "gift_card_brand",
  ] },
  
  // Source metadata
  { key: "source_type", label: "Source Type", category: "source_meta", paths: ["source_type", "canonical_deal_json.source_type"] },
  { key: "confidence", label: "Confidence", category: "source_meta", paths: ["confidence"] },
  { key: "region", label: "Region", category: "source_meta", paths: ["region", "canonical_deal_json.region"] },
  { key: "flyer_page_number", label: "Flyer Page #", category: "source_meta", paths: ["flyer_page_number", "canonical_deal_json.flyer_page_number"] },
];

// ================================================================
// FIELD RESOLUTION HELPERS
// ================================================================

/**
 * Get a nested value from an object using dot notation path.
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  
  return current;
}

/**
 * Resolve a field value from a raw deal input.
 * Tries each path in order until a valid value is found.
 */
function resolveFieldValue(
  input: RawDealInput,
  paths: string[]
): { value: unknown; sourcePath: string } {
  for (const path of paths) {
    const value = getNestedValue(input as unknown as Record<string, unknown>, path);
    if (isSafeValue(value)) {
      return { value, sourcePath: path };
    }
  }
  return { value: undefined, sourcePath: paths[0] || "unknown" };
}

/**
 * Determine field status based on value.
 */
function getFieldStatus(value: unknown): FieldStatus {
  if (value === null) return "null";
  if (value === undefined) return "missing";
  if (typeof value === "string" && value.trim() === "") return "missing";
  if (!isSafeValue(value)) return "missing";
  return "present";
}

// ================================================================
// MAIN FUNCTION: BUILD FIELD MAP
// ================================================================

/**
 * Build a comprehensive field map for a deal.
 * 
 * @param rawDeal - The raw deal input (from API or DB)
 * @param resolvedDeal - The resolved UIDeal (after toUiDeal transformation)
 * @returns Array of FieldDebug objects for all tracked fields
 */
export function buildFieldMap(
  rawDeal: RawDealInput,
  resolvedDeal?: UIDeal
): FieldDebug[] {
  const fieldMap: FieldDebug[] = [];
  
  for (const def of FIELD_DEFINITIONS) {
    // First try to get from resolved deal (already transformed)
    let value: unknown;
    let sourcePath: string;
    
    if (resolvedDeal && def.key in resolvedDeal) {
      value = (resolvedDeal as unknown as Record<string, unknown>)[def.key];
      sourcePath = `UIDeal.${def.key}`;
    } else {
      // Fall back to raw resolution
      const resolved = resolveFieldValue(rawDeal, def.paths);
      value = resolved.value;
      sourcePath = resolved.sourcePath;
    }
    
    const status = getFieldStatus(value);
    
    fieldMap.push({
      key: def.key,
      label: def.label,
      value,
      status,
      sourcePath,
      category: def.category,
      notes: def.required && status !== "present" 
        ? "Required field is missing" 
        : undefined,
    });
  }
  
  return fieldMap;
}

/**
 * Get fields grouped by category.
 */
export function getFieldsByCategory(
  fieldMap: FieldDebug[]
): Record<FieldCategory, FieldDebug[]> {
  const grouped: Record<FieldCategory, FieldDebug[]> = {
    header: [],
    value: [],
    dates: [],
    urls: [],
    fine_print: [],
    brands: [],
    source_meta: [],
  };
  
  for (const field of fieldMap) {
    grouped[field.category].push(field);
  }
  
  return grouped;
}

/**
 * Get summary statistics for a field map.
 */
export function getFieldSummary(fieldMap: FieldDebug[]): {
  present: number;
  missing: number;
  null: number;
  total: number;
} {
  return {
    present: fieldMap.filter(f => f.status === "present").length,
    missing: fieldMap.filter(f => f.status === "missing").length,
    null: fieldMap.filter(f => f.status === "null").length,
    total: fieldMap.length,
  };
}

/**
 * Get fields that are required for a template but missing from the deal.
 * 
 * @param fieldMap - The field map for the deal
 * @param enabledSections - Object of enabled section keys
 * @returns Fields that should warn the designer
 */
export function getTemplateWarnings(
  fieldMap: FieldDebug[],
  enabledSections: Record<string, boolean>
): { section: string; missingField: string; message: string }[] {
  const warnings: { section: string; missingField: string; message: string }[] = [];
  
  // CR292: Check if deal is ONGOING (no dates by design)
  const dateModeField = fieldMap.find(f => f.key === "date_mode");
  const datesTypeField = fieldMap.find(f => f.key === "dates_type");
  const datePhraseField = fieldMap.find(f => f.key === "date_phrase");
  
  const isOngoing = dateModeField?.value === "ONGOING";
  const isEvergreen = datesTypeField?.value === "EVERGREEN";
  const hasDatePhrase = datePhraseField?.status === "present" && datePhraseField.value;
  
  // Map sections to their required fields
  const sectionRequirements: Record<string, string[]> = {
    reward_box: ["bonus_value_cad", "points_amount"],
    qualifying_spend_box: ["qualifying_spend_cad"],
    effective_value_box: ["computed_value_cad", "effective_percent_back", "discount_percent"],
    dates: ["valid_from", "valid_until"],
    headline: ["headline_mechanic"],
  };
  
  for (const [section, requiredFields] of Object.entries(sectionRequirements)) {
    if (!enabledSections[section]) continue;
    
    // CR292: Skip dates warning for ONGOING/EVERGREEN deals
    if (section === "dates" && (isOngoing || isEvergreen || hasDatePhrase)) {
      continue;
    }
    
    // Check if at least one of the required fields is present
    const hasAnyRequired = requiredFields.some(fieldKey => {
      const field = fieldMap.find(f => f.key === fieldKey);
      return field?.status === "present";
    });
    
    if (!hasAnyRequired) {
      warnings.push({
        section,
        missingField: requiredFields.join(" or "),
        message: `"${section}" is enabled but requires ${requiredFields.join(" or ")}`,
      });
    }
  }
  
  // CR301: Special handling for claim_button - accept http(s) OR flyer:// URLs
  if (enabledSections["claim_button"]) {
    const claimUrlField = fieldMap.find(f => f.key === "claim_url");
    const dealUrlField = fieldMap.find(f => f.key === "deal_url");
    const flyerDeeplinkField = fieldMap.find(f => f.key === "flyer_deeplink_url");
    
    const hasValidClaimTarget = [claimUrlField, dealUrlField, flyerDeeplinkField].some(field => {
      if (field?.status !== "present") return false;
      const value = String(field.value || "");
      // Accept http(s):// or flyer:// URLs
      return value.startsWith("http://") || value.startsWith("https://") || value.startsWith("flyer://");
    });
    
    if (!hasValidClaimTarget) {
      warnings.push({
        section: "claim_button",
        missingField: "claim_url or deal_url or flyer_deeplink_url",
        message: `"claim_button" is enabled but no valid claim target (http or flyer URL) found`,
      });
    }
  }
  
  return warnings;
}
